/**
 * Services Hooks
 * 
 * This module provides React Query hooks for fetching services data.
 * It includes functionality for retrieving services from a local data source.
 * 
 * Features:
 * - Local service data retrieval
 * - Type-safe service data
 * - React Query integration
 */

import { useQuery } from '@tanstack/react-query';
import { services } from '@/data/services';
import { Service } from '@/types/service.types';

/**
 * Hook for fetching services data
 * @returns {UseQueryResult<Service[]>} Query result containing array of services
 * 
 * @example
 * const { data: services, isPending } = useGetServices();
 * 
 * if (isPending) {
 *   return <LoadingSpinner />;
 * }
 * 
 * return (
 *   <div>
 *     {services.map(service => (
 *       <ServiceCard key={service.id} service={service} />
 *     ))}
 *   </div>
 * );
 */
export function useGetServices() {
  return useQuery<Service[]>({
    queryKey: ['services'],
    queryFn: () => Promise.resolve(services),
    initialData: services,
    staleTime: Infinity,
  });
} 